// ============================================
// 2. CompletedTrades.js - Closed Positions
// ============================================

import React, { useState } from 'react';

const CompletedTrades = () => {
  const [completedTrades, setCompletedTrades] = useState([
    {
      id: 1,
      ticker: 'AAPL',
      shares: 100,
      entryPrice: 175.50,
      exitPrice: 183.25,
      entryDate: '2025-07-15',
      exitDate: '2025-07-22',
      daysHeld: 7,
      pnl: 775.00,
      pnlPercent: 4.42,
      strategy: 'Options Core',
      entryCatalyst: 'iPhone 16 Leak + Services Growth',
      exitReason: 'Target Hit - Earnings Risk',
      aiScore: 8.5,
      riskAmount: 500,
      winLoss: 'Win',
      lessons: 'Great timing on exit before earnings volatility. Catalyst was stronger than expected.',
      faithPrinciple: 'Toughness - Stuck to exit plan despite FOMO'
    },
    {
      id: 2,
      ticker: 'MSFT',
      shares: 50,
      entryPrice: 335.80,
      exitPrice: 328.45,
      entryDate: '2025-07-10',
      exitDate: '2025-07-18',
      daysHeld: 8,
      pnl: -367.50,
      pnlPercent: -2.19,
      strategy: 'Government Intel',
      entryCatalyst: 'Cloud Security Contract Announcement',
      exitReason: 'Stop Loss Hit - Broader Tech Selloff',
      aiScore: 7.8,
      riskAmount: 400,
      winLoss: 'Loss',
      lessons: 'Good risk management. Exit was correct despite strong catalyst - market timing matters.',
      faithPrinciple: 'Accountability - Admitted mistake, followed rules'
    },
    {
      id: 3,
      ticker: 'BABA',
      shares: 75,
      entryPrice: 89.20,
      exitPrice: 96.75,
      entryDate: '2025-07-08',
      exitDate: '2025-07-12',
      daysHeld: 4,
      pnl: 566.25,
      pnlPercent: 8.46,
      strategy: 'Phoenix Scalp',
      entryCatalyst: 'Regulatory Relief News + Gap Up',
      exitReason: 'Target Hit - Quick Scalp Success',
      aiScore: 9.2,
      riskAmount: 300,
      winLoss: 'Win',
      lessons: 'Perfect execution on Phoenix setup. Gap fade and VWAP reclaim worked perfectly.',
      faithPrinciple: 'Humility - Took profits quickly, didn\'t get greedy'
    },
    {
      id: 4,
      ticker: 'TSLA',
      shares: 30,
      entryPrice: 245.60,
      exitPrice: 238.90,
      entryDate: '2025-07-05',
      exitDate: '2025-07-09',
      daysHeld: 4,
      pnl: -201.00,
      pnlPercent: -2.73,
      strategy: 'Options Core',
      entryCatalyst: 'Cybertruck Production Update',
      exitReason: 'Stop Loss Hit - Musk Twitter Controversy',
      aiScore: 6.9,
      riskAmount: 350,
      winLoss: 'Loss',
      lessons: 'Catalyst was weak, should have waited for stronger setup. Twitter risk is real.',
      faithPrinciple: 'Integrity - Honest about weak entry thesis'
    },
    {
      id: 5,
      ticker: 'NVDA',
      shares: 40,
      entryPrice: 432.10,
      exitPrice: 448.75,
      entryDate: '2025-07-01',
      exitDate: '2025-07-03',
      daysHeld: 2,
      pnl: 666.00,
      pnlPercent: 3.85,
      strategy: 'Phoenix Scalp',
      entryCatalyst: 'AI Partnership + Volume Surge',
      exitReason: 'Target Hit - Perfect Phoenix Setup',
      aiScore: 9.5,
      riskAmount: 450,
      winLoss: 'Win',
      lessons: 'Textbook Phoenix execution. Entry at VWAP, volume confirmed, quick exit.',
      faithPrinciple: 'Family - Trade supported family time, didn\'t overtrade'
    }
  ]);

  const [selectedTrade, setSelectedTrade] = useState(null);
  const [filterStrategy, setFilterStrategy] = useState('All');
  const [filterOutcome, setFilterOutcome] = useState('All');

  // Helper Functions
  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);

  const formatPercent = (value) => `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;

  // Filter trades
  const filteredTrades = completedTrades.filter(trade => {
    const strategyMatch = filterStrategy === 'All' || trade.strategy === filterStrategy;
    const outcomeMatch = filterOutcome === 'All' || trade.winLoss === filterOutcome;
    return strategyMatch && outcomeMatch;
  });

  // Calculate statistics
  const totalTrades = filteredTrades.length;
  const winningTrades = filteredTrades.filter(t => t.winLoss === 'Win').length;
  const winRate = totalTrades > 0 ? (winningTrades / totalTrades * 100) : 0;
  const totalPnL = filteredTrades.reduce((sum, trade) => sum + trade.pnl, 0);
  const avgReturn = filteredTrades.reduce((sum, trade) => sum + trade.pnlPercent, 0) / totalTrades || 0;

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      backgroundColor: '#1a2332',
      color: '#ffffff',
      fontFamily: 'Georgia, serif',
      padding: '24px'
    }}>
      {/* WATERMARK */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: '0.03',
        pointerEvents: 'none',
        zIndex: '1',
        width: '800px',
        height: '800px'
      }}>
        <img 
          src="/finance-watermark.jpg"
          alt="Financial Trading Watermark"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </div>

      <div style={{ position: 'relative', zIndex: '2', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px'
        }}>
          <div>
            <h1 style={{
              color: '#ffffff',
              fontSize: '32px',
              fontWeight: 'bold',
              margin: '0',
              fontFamily: 'Georgia, serif'
            }}>📋 Completed Trades</h1>
            <p style={{
              color: '#a0a9c1',
              fontSize: '16px',
              margin: '8px 0 0 0',
              fontFamily: 'Georgia, serif'
            }}>Historical performance and trade analysis</p>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '24px',
          alignItems: 'center'
        }}>
          <div>
            <label style={{ color: '#a0a9c1', fontSize: '14px', marginRight: '8px', fontFamily: 'Georgia, serif' }}>
              Strategy:
            </label>
            <select 
              value={filterStrategy}
              onChange={(e) => setFilterStrategy(e.target.value)}
              style={{
                backgroundColor: '#202A44',
                color: '#ffffff',
                border: '1px solid #3a4a6b',
                borderRadius: '4px',
                padding: '8px',
                fontFamily: 'Georgia, serif'
              }}
            >
              <option value="All">All Strategies</option>
              <option value="Options Core">Options Core</option>
              <option value="Government Intel">Government Intel</option>
              <option value="Phoenix Scalp">Phoenix Scalp</option>
            </select>
          </div>
          <div>
            <label style={{ color: '#a0a9c1', fontSize: '14px', marginRight: '8px', fontFamily: 'Georgia, serif' }}>
              Outcome:
            </label>
            <select 
              value={filterOutcome}
              onChange={(e) => setFilterOutcome(e.target.value)}
              style={{
                backgroundColor: '#202A44',
                color: '#ffffff',
                border: '1px solid #3a4a6b',
                borderRadius: '4px',
                padding: '8px',
                fontFamily: 'Georgia, serif'
              }}
            >
              <option value="All">All Outcomes</option>
              <option value="Win">Wins Only</option>
              <option value="Loss">Losses Only</option>
            </select>
          </div>
        </div>

        {/* Statistics Summary */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <div style={{
            backgroundColor: '#202A44',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b',
            textAlign: 'center'
          }}>
            <div style={{ color: '#a0a9c1', fontSize: '14px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
              Total Trades
            </div>
            <div style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
              {totalTrades}
            </div>
          </div>
          <div style={{
            backgroundColor: '#202A44',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b',
            textAlign: 'center'
          }}>
            <div style={{ color: '#a0a9c1', fontSize: '14px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
              Win Rate
            </div>
            <div style={{
              color: winRate >= 60 ? '#00FF5F' : winRate >= 40 ? '#FFB800' : '#FF0000',
              fontSize: '24px',
              fontWeight: 'bold',
              fontFamily: 'Georgia, serif'
            }}>
              {winRate.toFixed(1)}%
            </div>
          </div>
          <div style={{
            backgroundColor: '#202A44',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b',
            textAlign: 'center'
          }}>
            <div style={{ color: '#a0a9c1', fontSize: '14px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
              Total P&L
            </div>
            <div style={{
              color: totalPnL > 0 ? '#00FF5F' : '#FF0000',
              fontSize: '24px',
              fontWeight: 'bold',
              fontFamily: 'Georgia, serif'
            }}>
              {formatCurrency(totalPnL)}
            </div>
          </div>
          <div style={{
            backgroundColor: '#202A44',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b',
            textAlign: 'center'
          }}>
            <div style={{ color: '#a0a9c1', fontSize: '14px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
              Avg Return
            </div>
            <div style={{
              color: avgReturn > 0 ? '#00FF5F' : '#FF0000',
              fontSize: '24px',
              fontWeight: 'bold',
              fontFamily: 'Georgia, serif'
            }}>
              {formatPercent(avgReturn)}
            </div>
          </div>
        </div>

        {/* Trade Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
          gap: '20px'
        }}>
          {filteredTrades.map((trade, index) => (
            <div key={trade.id} style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#2B3554'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#202A44'}
            onClick={() => setSelectedTrade(trade)}
            >
              {/* Trade Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontSize: '20px',
                    fontFamily: 'Georgia, serif'
                  }}>
                    {trade.ticker}
                  </div>
                  <div style={{
                    backgroundColor: trade.winLoss === 'Win' ? '#00FF5F' : '#FF0000',
                    color: '#ffffff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: 'Georgia, serif'
                  }}>
                    {trade.winLoss}
                  </div>
                  <div style={{
                    backgroundColor: '#5BC0DE',
                    color: '#ffffff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: 'Georgia, serif'
                  }}>
                    {trade.strategy}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    color: trade.pnl > 0 ? '#00FF5F' : '#FF0000',
                    fontSize: '18px',
                    fontWeight: '600',
                    fontFamily: 'Georgia, serif'
                  }}>
                    {formatCurrency(trade.pnl)}
                  </div>
                  <div style={{
                    color: trade.pnl > 0 ? '#00FF5F' : '#FF0000',
                    fontSize: '14px',
                    fontFamily: 'Georgia, serif'
                  }}>
                    {formatPercent(trade.pnlPercent)}
                  </div>
                </div>
              </div>

              {/* Trade Details */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                fontSize: '14px',
                marginBottom: '12px'
              }}>
                <div>
                  <div style={{ color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Entry</div>
                  <div style={{ color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                    {formatCurrency(trade.entryPrice)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Exit</div>
                  <div style={{ color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                    {formatCurrency(trade.exitPrice)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Days Held</div>
                  <div style={{ color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                    {trade.daysHeld}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>AI Score</div>
                  <div style={{ color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                    {trade.aiScore}/10
                  </div>
                </div>
              </div>

              {/* Entry Catalyst */}
              <div style={{
                backgroundColor: '#1a2332',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '12px'
              }}>
                <div style={{
                  color: '#D4AF37',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '4px',
                  fontFamily: 'Georgia, serif'
                }}>
                  Entry Catalyst
                </div>
                <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
                  {trade.entryCatalyst}
                </div>
              </div>

              {/* Exit Reason */}
              <div style={{
                backgroundColor: '#1a2332',
                padding: '12px',
                borderRadius: '6px'
              }}>
                <div style={{
                  color: '#0069B1',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '4px',
                  fontFamily: 'Georgia, serif'
                }}>
                  Exit Reason
                </div>
                <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
                  {trade.exitReason}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trade Detail Modal */}
        {selectedTrade && (
          <div style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: '1000'
          }}>
            <div style={{
              backgroundColor: '#202A44',
              padding: '32px',
              borderRadius: '12px',
              border: '1px solid #3a4a6b',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '24px'
              }}>
                <h2 style={{
                  color: '#ffffff',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  margin: '0',
                  fontFamily: 'Georgia, serif'
                }}>
                  {selectedTrade.ticker} Trade Analysis
                </h2>
                <button
                  onClick={() => setSelectedTrade(null)}
                  style={{
                    backgroundColor: '#FF0000',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontFamily: 'Georgia, serif'
                  }}
                >
                  Close
                </button>
              </div>

              {/* Complete Trade Details */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                
                {/* Performance Summary */}
                <div style={{
                  backgroundColor: '#1a2332',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                  <h3 style={{ color: '#D4AF37', margin: '0 0 12px 0', fontFamily: 'Georgia, serif' }}>
                    Performance Summary
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <div style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>P&L</div>
                      <div style={{
                        color: selectedTrade.pnl > 0 ? '#00FF5F' : '#FF0000',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        fontFamily: 'Georgia, serif'
                      }}>
                        {formatCurrency(selectedTrade.pnl)} ({formatPercent(selectedTrade.pnlPercent)})
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>Risk vs Reward</div>
                      <div style={{ color: '#ffffff', fontSize: '16px', fontFamily: 'Georgia, serif' }}>
                        Risk: {formatCurrency(selectedTrade.riskAmount)} | 
                        R/R: {(Math.abs(selectedTrade.pnl) / selectedTrade.riskAmount).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trade Details */}
                <div style={{
                  backgroundColor: '#1a2332',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                  <h3 style={{ color: '#D4AF37', margin: '0 0 12px 0', fontFamily: 'Georgia, serif' }}>
                    Trade Details
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '14px' }}>
                    <div>
                      <div style={{ color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Entry Date</div>
                      <div style={{ color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                        {new Date(selectedTrade.entryDate).toLocaleDateString()}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Exit Date</div>
                      <div style={{ color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                        {new Date(selectedTrade.exitDate).toLocaleDateString()}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Entry Price</div>
                      <div style={{ color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                        {formatCurrency(selectedTrade.entryPrice)}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Exit Price</div>
                      <div style={{ color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                        {formatCurrency(selectedTrade.exitPrice)}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Position Size</div>
                      <div style={{ color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                        {selectedTrade.shares} shares
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Days Held</div>
                      <div style={{ color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                        {selectedTrade.daysHeld} days
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lessons Learned */}
                <div style={{
                  backgroundColor: '#1a2332',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                  <h3 style={{ color: '#D4AF37', margin: '0 0 12px 0', fontFamily: 'Georgia, serif' }}>
                    Lessons Learned
                  </h3>
                  <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif', lineHeight: '1.5' }}>
                    {selectedTrade.lessons}
                  </div>
                </div>

                {/* FAITH Principle */}
                <div style={{
                  backgroundColor: '#1a2332',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                  <h3 style={{ color: '#D4AF37', margin: '0 0 12px 0', fontFamily: 'Georgia, serif' }}>
                    FAITH Principle Applied
                  </h3>
                  <div style={{ color: '#ffffff', fontSize: '14px', fontFamily: 'Georgia, serif', lineHeight: '1.5' }}>
                    {selectedTrade.faithPrinciple}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompletedTrades;
